import CreateAccountModal from "@app/components/account-modal";
import Button from "@app/components/button";
import ConfirmModal from "@app/components/confirm-modal";
import Pagination from "@app/components/pagination";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEFAULT_PAGE,
  DEFAULT_TOTAL_PAGES,
  SystemMessage,
} from "@app/constants";
import AccountService from "@app/services/http/account.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Account, AccountResponse } from "@app/types";
import { formatDate } from "@app/utils/util";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import useObservable from "@core/hooks/use-observable.hook";
import { useEffect, useState } from "react";

function CompanyManagement() {
  const [page, setPages] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [accounts, setAccount] = useState<Account[]>([]);

  const [update, forceUpdate] = useForceUpdate();

  const { subscribeOnce, subscribeUntilDestroy } = useObservable();

  useEffect(() => {
    subscribeUntilDestroy(
      AccountService.getAccounts<AccountResponse>(page),
      (res) => {
        if (res.data.length) {
          setAccount(res.data);
          setTotalPages(res.total_page);
        } else {
          setAccount([]);
          setTotalPages(DEFAULT_TOTAL_PAGES);
        }
      }
    );
  }, [update, page]);

  const handleCreateCompany = () => {
    const createAccountModalObs = openPortalDialog(CreateAccountModal);

    createAccountModalObs.afterClosed().subscribe((data) => {
      if (data?.account) {
        addToast({ text: SystemMessage.SUCCESS });
        forceUpdate();
      }
    });
  };

  const handleUpdateAccount = (id: string, account: Account) => {
    const updateAccountObs = openPortalDialog(CreateAccountModal, { account });

    updateAccountObs.afterClosed().subscribe((data) => {
      if (data.account) {
        addToast({ text: SystemMessage.SUCCESS });
        forceUpdate();
      }
    });
  };

  const handleDeleteAccount = (id: string) => {
    const deleteAccountObs = openPortalDialog(ConfirmModal, {
      message: "Bạn có chắc chắn xóa công ty này không?",
    });

    deleteAccountObs.afterClosed().subscribe((data) => {
      if (data?.isAccept) {
        subscribeOnce(AccountService.deleteAccount(id), () => {
          addToast({ text: SystemMessage.DELETE_SUCCESS });
          forceUpdate();
        });
      }
    });
  };

  const onPageChange = (page: number) => {
    setPages(page);
  };

  return (
    <div className="w-full mx-auto mt-4 pb-4">
      <div className="flex items-center justify-end pb-2 px-2 border-b border-[#409EFF]">
        <Button
          label="Tạo Công Ty"
          width="fit-content"
          className="px-2 text-sm"
          onClick={handleCreateCompany}
        />
      </div>
      <div className="px-2 overflow-x-auto mt-2">
        <div className="inline-block min-w-full shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full leading-normal">
            <thead className="text-white">
              <tr className="border-b-2 border-gray-200 bg-[#409EFF] text-sm">
                <th className="p-3 text-left w-52">ID</th>
                <th className="p-3 text-left w-72">Username</th>
                <th className="p-3 text-left w-32">Tên Công ty</th>
                <th className="p-3 text-left w-72">Thời gian tạo</th>
                <th className="p-3 text-left w-52">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {!!accounts.length &&
                accounts.map((item) => (
                  <tr
                    key={item._id}
                    className="border-b border-gray-200 text-sm hover:bg-[#51b83c2c]"
                  >
                    <td className="p-3">{item._id}</td>
                    <td className="p-3">{item.username}</td>
                    <td className="p-3">{item.name}</td>
                    <td className="p-3">{formatDate(item.createdAt)}</td>

                    <td className="p-3">
                      <Button
                        label="Xóa"
                        width="fit-content"
                        className="px-2 text-sm"
                        onClick={() => handleDeleteAccount(item._id)}
                      />
                      <Button
                        label="Sửa"
                        width="fit-content"
                        className="px-2 text-sm"
                        onClick={() => handleUpdateAccount(item._id, item)}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!accounts.length && (
            <div className="flex justify-center">Không có dữ liệu</div>
          )}

          {totalPages > 0 && (
            <div className="flex justify-center mt-3">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompanyManagement;
